import type { GccDashboardData } from '@shared/types'
import { GCC_DISPOSITIONS, GCC_OWNERSHIPS, GCC_PATHWAYS, GCC_PATHWAY_LABELS, GCC_QUICK_WIN_OFF_SUMMARY, GCC_WAVES, findValueCaseMismatches, findWaveViolations, gccTaxonomyReadSummary } from '@tn/shared'
import { fmtFte } from './GccResidualWorkload'
import { fmtFte2 } from './GccSourcingModel'
import { OWNER_LABEL, RATE_MATCH_LABEL, fmtUsdExact } from './GccValueCase'

/** A deterministic calculation the pipeline ran in code after an AI call. */
export interface GccTraceCalculation {
  formula: string
  rows: [label: string, value: string][]
}

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0)

/** Defaulted task ids listed before the rest are summarised as "…" */
const MAX_LISTED_IDS = 10

const listIds = (ids: string[]) =>
  ids.length > 0 ? `${ids.length} (${ids.slice(0, MAX_LISTED_IDS).join(', ')}${ids.length > MAX_LISTED_IDS ? ', …' : ''})` : '0'

/**
 * Pipeline Traceability entries for the work done in code, not by the LLM: the
 * Step 0 baseline FTE and the Step 1 residual workload (WI 2323), the Step 2
 * sourcing allocation (WI 2324), the Step 3 pathways and waves (WI 2325), and the
 * Step 4 value case (WI 2326). Keyed by pipeline step, so the trace panel can
 * show each one under the AI call it follows. Designs saved before those work
 * items don't have the data, so they get no entry for that step.
 */
export function gccTraceCalculations(gcc: GccDashboardData | undefined): Partial<Record<0 | 1 | 2 | 3 | 4, GccTraceCalculation>> {
  const out: Partial<Record<0 | 1 | 2 | 3 | 4, GccTraceCalculation>> = {}
  const register = gcc?.taskRegister?.taskRegister ?? []
  const taxonomyRead = gcc?.taskRegister?.taxonomyRead

  const roles = register.flatMap(p => p.roles ?? [])
  if (register.some(p => Array.isArray(p.roles))) {
    const fromDocs = roles.filter(r => r.fteSource === 'Client-doc')
    const estimated = roles.filter(r => r.fteSource !== 'Client-doc')
    const fteOf = (rs: typeof roles) => fmtFte(Math.round(sum(rs.map(r => r.fte)) * 100) / 100)
    out[0] = {
      formula: "Task FTE = role FTE × timePct / 100, after each role's timePct is rebalanced to 100%",
      rows: [
        ['Processes', String(register.length)],
        ['Roles', String(roles.length)],
        ['Tasks', String(sum(register.map(p => p.tasks.length)))],
        ['Baseline FTE', fteOf(roles)],
        ['FTE from client documents', `${fteOf(fromDocs)} (${fromDocs.length} role${fromDocs.length !== 1 ? 's' : ''})`],
        ['FTE estimated by the LLM', `${fteOf(estimated)} (${estimated.length} role${estimated.length !== 1 ? 's' : ''})`],
        // WI 2888: absent on designs saved before it, and when no taxonomy was uploaded
        ...(taxonomyRead ? [['Taxonomy text read', gccTaxonomyReadSummary(taxonomyRead)]] as [string, string][] : []),
      ],
    }
  }

  const residual = gcc?.taggedTasks?.residualWorkload
  // Quick wins (WI 2806): absent on designs saved before them
  const horizon = gcc?.taggedTasks?.quickWinHorizonMonths
  if (residual) {
    const tags = gcc?.taggedTasks?.taggedTasks ?? []
    const defaulted = residual.defaultedTaskIds
    out[1] = {
      formula: 'Residual FTE = task FTE × residual % for its disposition, summed per capability (Step 0 process)'
        + (horizon === 0 ? `. ${GCC_QUICK_WIN_OFF_SUMMARY}` // WI 2886
          : horizon !== undefined ? `. Quick win = Automate or Agentify, Low complexity and live within ${horizon} months` : ''),
      rows: [
        ['Residual % by disposition', GCC_DISPOSITIONS.map(d => `${d} ${residual.residualPctByDisposition[d]}%`).join(' · ')],
        ['Tasks by disposition', GCC_DISPOSITIONS.map(d => `${d} ${tags.filter(t => t.disposition === d).length}`).join(' · ')],
        ['Baseline → residual FTE', `${fmtFte(residual.totalBaselineFte)} → ${fmtFte(residual.totalResidualFte)} (${residual.totalResidualPct}%)`],
        ['Capabilities', String(residual.capabilities.length)],
        ['Tasks defaulted to Human', listIds(defaulted)],
        ...(horizon === 0
          ? [['Quick wins', 'Off (0-month horizon)']] as [string, string][]
          : horizon !== undefined
          ? [
              [`Quick wins (≤ ${horizon} months), agentify first`, listIds(gcc?.taggedTasks?.quickWinTaskIds ?? [])],
              ['Automate / Agentify tasks with no valid complexity or months (not flagged)', listIds(gcc?.taggedTasks?.quickWinUnscoredTaskIds ?? [])],
            ] as [string, string][]
          : []),
      ],
    }
  }

  const stage2 = gcc?.sourcingModel
  const caps = stage2?.sourcingModel ?? []
  if (caps.some(c => Array.isArray(c.allocation))) {
    const parts = caps.flatMap(c => c.allocation ?? [])
    const cents = (values: number[]) => Math.round(sum(values.map(v => v * 100)))
    const residualCents = cents(caps.map(c => c.residualFte ?? 0))
    const allocatedCents = cents(parts.map(a => a.fte))
    out[2] = {
      formula: 'Capability split = Σ residual FTE of its tasks per ownership × location, scaled to the capability’s Step 1 residual and rounded to 0.01 FTE (largest remainder)',
      rows: [
        ['Tasks sourced', String(stage2?.taskSourcing?.length ?? 0)],
        ['Tasks with no residual work (not sourced)', String(stage2?.skippedTaskIds?.length ?? 0)],
        ['Tasks defaulted to Retained · HQ', listIds(stage2?.defaultedTaskIds ?? [])],
        ...(stage2?.automateFirstThresholdPct !== undefined
          ? [[`Automate first (AI releases ≥ ${stage2.automateFirstThresholdPct}%), kept Retained`, listIds(stage2.automateFirstTaskIds ?? [])] as [string, string]]
          : []),
        ['Residual FTE by ownership', GCC_OWNERSHIPS.map(o => `${o} ${fmtFte2(cents(parts.filter(a => a.ownership === o).map(a => a.fte)) / 100)}`).join(' · ')],
        ['Allocated vs residual FTE', `${fmtFte2(allocatedCents / 100)} of ${fmtFte2(residualCents / 100)}${allocatedCents === residualCents ? ' ✓' : ''}`],
        ['Hybrid capabilities', `${caps.filter(c => c.isHybrid).length} of ${caps.length}`],
      ],
    }
  }

  const stage3 = gcc?.sequencing
  const seqTasks = stage3?.taskSequencing
  if (seqTasks) {
    const seqCaps = stage3?.sequencing ?? []
    const violations = findWaveViolations(seqTasks)
    const quickWins = seqTasks.filter(t => t.quickWin)
    out[3] = {
      formula: 'Wave = score band (4–5 → W1, 3 → W2, 1–2 → W3): transform wave from AI readiness, migration wave from transition feasibility. Pathway from the two waves (T < M Transform First, M < T Shift First, equal Together). Capability: baseline-FTE-weighted most common pathway, earliest task waves'
        + (horizon !== undefined ? '. Quick wins: transform wave no later than the wave of their months to implement, migration after it' : ''),
      rows: [
        ['Tasks sequenced', String(seqTasks.length)],
        ['Tasks by pathway', GCC_PATHWAYS.map(p => `${GCC_PATHWAY_LABELS[p]} ${seqTasks.filter(t => t.pathway === p).length}`).join(' · ')],
        ['FTE transformed / migrated by wave', GCC_WAVES.map(w => {
          const f = stage3?.waveFlow?.find(x => x.wave === w)
          return `W${w} ${fmtFte2(f?.transformFte ?? 0)} / ${fmtFte2(f?.migrationFte ?? 0)}`
        }).join(' · ')],
        ['Tasks defaulted to Wave 3', listIds(stage3?.defaultedTaskIds ?? [])],
        ['Wave rule check', violations.length === 0 ? 'All tasks consistent ✓' : `${listIds(violations)} inconsistent`],
        ['Mixed capabilities', `${seqCaps.filter(c => c.isMixed).length} of ${seqCaps.length}`],
        ...(horizon !== undefined
          ? [
              ['Quick wins Transform First / Retain & Transform', `${quickWins.filter(t => t.pathway === 'TransformFirst' || t.pathway === 'RetainAndTransform').length} of ${quickWins.length}`],
              ['Quick-win waves moved by the agentify-first rule', listIds(quickWins.filter(t => t.quickWinAdjusted).map(t => t.taskId))],
            ] as [string, string][]
          : []),
      ],
    }
  }

  const vc = gcc?.valueCase
  if (vc) {
    const t = vc.totals
    const a = vc.assumptions
    const mismatches = findValueCaseMismatches(vc)
    const matches = (['exact', 'band', 'floor', 'missing'] as const)
      .map(m => [m, a.payRates.filter(r => r.match === m).length] as const)
      .filter(([, n]) => n > 0)
      .map(([m, n]) => `${RATE_MATCH_LABEL[m]} ${n}`)
    out[4] = {
      formula: 'Per task, AI first: AI saving = AI-released FTE × current rate; location saving = residual FTE × (current rate − end-state rate), 0 for Retained work; total = AI + location = current cost − end-state cost. Whole USD per task, summed per capability. The LLM only tags roles and lists skills',
      rows: [
        ['Pay rates', `${a.payRateSource === 'default' ? 'Default benchmark table' : 'Custom table'}: ${a.payRates.length} rate${a.payRates.length !== 1 ? 's' : ''} used${matches.length ? ` (${matches.join(' · ')})` : ''}`],
        ['Current → AI released → residual FTE', `${fmtFte2(t.currentFte)} → ${fmtFte2(t.aiReleasedFte)} → ${fmtFte2(t.residualFte)}`],
        ['Future FTE by owner', GCC_OWNERSHIPS.map(o => `${OWNER_LABEL[o]} ${fmtFte2(t.futureFteByOwnership[o])}`).join(' · ')],
        ['Current → end-state cost', `${fmtUsdExact(t.currentCostUSD)} → ${fmtUsdExact(t.endStateCostUSD)}`],
        ['AI + location = total saving', `${fmtUsdExact(t.aiSavingUSD)} + ${fmtUsdExact(t.locationSavingUSD)} = ${fmtUsdExact(t.totalSavingUSD)}`],
        ['Run-rate after each wave', vc.waves.map(w => `W${w.wave} ${fmtUsdExact(w.cumulativeSavingUSD)}`).join(' · ')],
        ['Roles defaulted to HQ', String(a.defaultedLocationRoles.length)],
        ['Tasks with no current rate (not priced)', listIds(a.unpricedTaskIds)],
        ['Tasks with no end-state rate (no location saving)', listIds(a.endRateFallbackTaskIds)],
        ['Reconciliation check', mismatches.length === 0 ? 'All figures reconcile ✓' : `${mismatches.length} mismatch${mismatches.length !== 1 ? 'es' : ''}: ${mismatches.slice(0, 3).join('; ')}`],
      ],
    }
  }
  return out
}
